'use client'

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { HmrcTokenExpiredError, useHMRCITSAStore } from '@repo/store/useHMRCITSAStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { isValidNI } from '@repo/validation/isValidNI'
import { TextEntry } from '@repo/ui/TextEntry'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

// The OAuth callback (`HmrcMtdItsa/GetToken`, called by HMRC directly — never this app)
// redirects back here with `?success=true|false`. queryParams (not react-router's
// useSearchParams) is this app's established source for query params on the hash router
// — see the FormEvo setup/callback screens (@repo/third-party-forms-components) for the
// same pattern. Values always arrive as strings (see useUrlStore.handleUrlUpdate).
const readOAuthSuccess = (queryParams: Record<string, unknown> | undefined): boolean | null => {
  const success = queryParams?.success
  if (success === true || success === 'true') return true
  if (success === false || success === 'false') return false
  return null
}

export const SetupModule = () => {
  const { getHmrcLoginUrl } = useHMRCITSAStore()
  // Estate pattern (see CaseTypeMappingModule): self-supplied top clearance when the layout
  // hides the PageTitleBlock on reduced-height windows (the breadcrumb bar is position:fixed).
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { queryParams } = useUrlStore()
  const location = useLocation()

  const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState('')
  const [niTouched, setNiTouched] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectError, setConnectError] = useState('')
  // Other HMRC-dependent screens catch HmrcTokenExpiredError and navigate back here with
  // this flag (mirroring the prototype's location.state.tokenExpired) so we can prompt a
  // reconnect; we also catch it directly below in case GetLoginUrl itself ever 498s.
  const [tokenExpired, setTokenExpired] = useState(
    (location.state as { tokenExpired?: boolean } | null)?.tokenExpired === true
  )

  useEffect(() => {
    if ((location.state as { tokenExpired?: boolean } | null)?.tokenExpired === true) {
      setTokenExpired(true)
    }
  }, [location.state])

  const oauthSuccess = readOAuthSuccess(queryParams)
  const isValidNumber = isValidNI(nationalInsuranceNumber)
  const showNiError = niTouched && nationalInsuranceNumber.length > 0 && !isValidNumber

  const handleConnect = async () => {
    setConnectError('')
    setIsConnecting(true)
    try {
      const loginUrl = await getHmrcLoginUrl({ nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase() })
      if (loginUrl) {
        window.location.href = loginUrl
      } else {
        setConnectError('Unable to retrieve the HMRC login URL. Please try again.')
      }
    } catch (error) {
      if (error instanceof HmrcTokenExpiredError) {
        setTokenExpired(true)
      } else {
        setConnectError((error as Error)?.message || 'An error occurred while connecting to HMRC. Please try again.')
      }
    } finally {
      setIsConnecting(false)
    }
  }

  return (
    <MUIStack
      data-testid="SetupModule"
      gap={6}
      sx={{
        maxWidth: 680,
        margin: 'auto',
        mt: pageTitleBlockIsVisible ? 'auto' : '12rem',
        backgroundColor: 'white',
        borderRadius: '6px',
        padding: '60px 40px'
      }}
    >
      {tokenExpired && (
        <MUIAlert severity="warning" data-testid="SetupModuleTokenExpiredBanner">
          Your HMRC access has expired. Please re-enter your National Insurance Number and reconnect your account.
        </MUIAlert>
      )}

      {oauthSuccess === true ? (
        <MUIAlert severity="success" data-testid="SetupModuleSuccessBanner">
          Your HMRC account has been connected successfully.
        </MUIAlert>
      ) : (
        <>
          {oauthSuccess === false && (
            <MUIAlert severity="error" data-testid="SetupModuleFailureBanner">
              There was an error connecting to HMRC. Access was not granted. Please try again.
            </MUIAlert>
          )}

          <MUITypography variant="body1">
            {`This screen enables you to connect your HMRC Making Tax Digital for Income Tax Self Assessment (MTD ITSA) account to Osprey.`}
          </MUITypography>

          <MUITypography variant="body1">
            {`Enter your National Insurance Number below and click Connect. You will be redirected to HMRC to grant access. Once authorised, you will be redirected back to Osprey.`}
          </MUITypography>

          <TextEntry
            name="nationalInsuranceNumber"
            label="National Insurance Number"
            value={nationalInsuranceNumber}
            onChange={(value: string) => setNationalInsuranceNumber((value || '').toUpperCase())}
            onBlur={() => setNiTouched(true)}
            errors={
              showNiError ? { nationalInsuranceNumber: 'Enter a valid National Insurance Number (e.g. AB123456C)' } : {}
            }
            helperText="Format: e.g. AB123456C"
            disabled={isConnecting}
            limit={9}
            required
          />

          {connectError && <MUIAlert severity="error">{connectError}</MUIAlert>}

          <MUIButton
            variant="contained"
            color="primary"
            size="large"
            data-testid="SetupModuleConnectButton"
            onClick={handleConnect}
            disabled={!isValidNumber || isConnecting}
            sx={{ maxWidth: 200, width: '100%', margin: '20px 0 0 auto' }}
          >
            {isConnecting ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Connect to HMRC'}
          </MUIButton>
        </>
      )}
    </MUIStack>
  )
}
